import { useEffect, useMemo, useState } from 'react';
import type { Operation } from '@vigilo/shared';
import { get } from '@vigilo/api-client';

export interface OperationAlertSummary {
  total: number;
  active: number;
  open: number;
  acknowledged: number;
  escalated: number;
  resolved: number;
}

interface AlertListResponse {
  summary?: OperationAlertSummary;
}

export interface OperationAlertSummaryState {
  summariesByOperationId: Map<string, OperationAlertSummary>;
  loading: boolean;
  error: string | null;
}

const EMPTY_SUMMARY: OperationAlertSummary = {
  total: 0,
  active: 0,
  open: 0,
  acknowledged: 0,
  escalated: 0,
  resolved: 0,
};

function unwrapSummary(body: unknown): OperationAlertSummary {
  const direct = body as AlertListResponse;
  const envelope = body as { data?: AlertListResponse };
  return direct?.summary ?? envelope.data?.summary ?? EMPTY_SUMMARY;
}

function queryFor(operation: Operation): string {
  const params = new URLSearchParams();
  params.set('operationId', operation.id);
  params.set('zoneIds', operation.assignedZoneIds.join(','));
  params.set('page', '1');
  params.set('pageSize', '1');
  return params.toString();
}

export function useOperationAlertSummaries(
  operations: readonly Operation[],
  enabled = true,
  pollMs = 2000,
): OperationAlertSummaryState {
  const [summariesByOperationId, setSummariesByOperationId] = useState<Map<string, OperationAlertSummary>>(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operationScopeKey = useMemo(
    () => operations
      .map(operation => `${operation.id}:${operation.assignedZoneIds.join(',')}`)
      .sort()
      .join('|'),
    [operations],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (!enabled || operations.length === 0) {
      setSummariesByOperationId(new Map());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    async function loadSummaries(initial = false) {
      if (initial) setLoading(true);
      try {
        const entries = await Promise.all(operations.map(async operation => {
          const summary = operation.assignedZoneIds.length === 0
            ? EMPTY_SUMMARY
            : unwrapSummary(await get<unknown>(`/alerts?${queryFor(operation)}`));
          return [operation.id, summary] as const;
        }));
        if (!cancelled) {
          setSummariesByOperationId(new Map(entries));
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setSummariesByOperationId(new Map());
          setError('Unable to load operation alert summaries.');
        }
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    }

    void loadSummaries(true);
    const interval = window.setInterval(() => {
      void loadSummaries(false);
    }, Math.max(1000, pollMs));

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled, operationScopeKey, pollMs]);

  return { summariesByOperationId, loading, error };
}
